import { forwardRef } from 'react'

import { formatDistanceToNow, isToday, isBefore } from 'date-fns'

import { cn } from '../../..'
import { Tooltip, TooltipContent, TooltipTrigger } from '../tooltip/tooltip'

interface DateProps {
  className?: string
  date: string | Date
}

/**
 * Algorithm for now -
 * 1. If the date is today, show the time, with 'Today at' prefix
 * 2. If the date is more than a 1 month ago, show the relative date
 * 3. Otherwise, show the date, with the day of the week and the time
 */
const formatRelativeTime = (date: Date) => {
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const hour = (date.getHours() % 12 || 12).toString().padStart(2, '0')
  const amPm = date.getHours() >= 12 ? 'PM' : 'AM'

  if (isToday(date)) {
    return `Today at ${hour}:${minutes} ${amPm}`
  }

  // is more than a 1 month ago
  if (isBefore(date, new Date().setDate(new Date().getDate() - 28))) {
    return formatDistanceToNow(date, { addSuffix: true })
  }
  // Otherwise, show absolute date
  return `${date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })}, ${hour}:${minutes} ${amPm}`
}

/**
 * A date stamp component that displays a date in the format of "Jan 1".
 *
 * @example
 * ```tsx
 * <DateStamp date="2021-01-01" />
 * // "Jan 1"
 * ```
 *
 * ```tsx
 * <DateStamp date={new Date()} />
 * // "Jan 1"
 * ```
 */
export const DateStamp = forwardRef<HTMLButtonElement, DateProps>(
  ({ date, className }, ref) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date

    const formattedDate = formatRelativeTime(dateObj)

    const dateTime = dateObj.toLocaleString('en-US', {
      timeStyle: 'medium',
    })

    const formattedDateWithYear = dateObj.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })

    return (
      <Tooltip>
        <TooltipTrigger
          className="cursor-pointer text-nowrap text-subtle hover:text-default"
          ref={ref}
        >
          <time
            className={cn('font-input-s', className)}
            dateTime={dateObj.toISOString()}
          >
            {formattedDate}
          </time>
        </TooltipTrigger>
        <TooltipContent className="font-input-s">
          {formattedDateWithYear} {dateTime}
        </TooltipContent>
      </Tooltip>
    )
  },
)

DateStamp.displayName = 'DateStamp'
